<script setup>
import {computed, onMounted, ref, watch} from "vue";

const emit = defineEmits(['chapter-completion-update'])

const props = defineProps({
    courseId: {
        type: Number,
        required: true
    },
    currentChapterId: {
        type: Number,
        required: false,
        default: null,
    }
})

const course = ref(null)
const chapterCompletion = ref(null)
const open = ref()
const expandedGroupIds = ref(new Set())
const contractAtGroupCount = 3
const contractAtChapterCount = 6

const shouldEnableCollapsibleGroups = computed(() => {
    if (!course.value) {
        return false;
    }

    const groups = course.value.chapter_groups ?? [];
    if (groups.length <= contractAtGroupCount) {
        return false;
    }

    const chapters = course.value.chapters ?? [];

    return groups.every((group) => chapters.filter((chapter) => chapter.course_chapter_group_id === group.id).length >= contractAtChapterCount);
});

const currentChapterGroupId = computed(() => {
    if (!course.value || !props.currentChapterId) {
        return null;
    }

    return (course.value.chapters ?? []).find((chapter) => chapter.id === props.currentChapterId)?.course_chapter_group_id ?? null;
});

const ensureCurrentChapterGroupExpanded = () => {
    if (!shouldEnableCollapsibleGroups.value || !currentChapterGroupId.value) {
        return;
    }

    const nextExpandedGroupIds = new Set(expandedGroupIds.value);
    nextExpandedGroupIds.add(currentChapterGroupId.value);
    expandedGroupIds.value = nextExpandedGroupIds;
}

const isGroupExpanded = (groupId) => {
    if (!shouldEnableCollapsibleGroups.value) {
        return true;
    }

    return expandedGroupIds.value.has(groupId);
};

const toggleGroupExpansion = (groupId) => {
    if (!shouldEnableCollapsibleGroups.value) {
        return;
    }

    const nextExpandedGroupIds = new Set(expandedGroupIds.value);
    if (nextExpandedGroupIds.has(groupId)) {
        nextExpandedGroupIds.delete(groupId);
    } else {
        nextExpandedGroupIds.add(groupId);
    }

    expandedGroupIds.value = nextExpandedGroupIds;
}

const loadChapters = async () => {
    const res = await axios.get(route('api.course.customerarea.chapters', props.courseId))

    course.value = res.data.course
    chapterCompletion.value = res.data.chapter_completions
    expandedGroupIds.value = new Set()
    ensureCurrentChapterGroupExpanded()
}

const displayRows = computed(() => {
    if (!course.value) {
        return [];
    }

    const chaptersById = new Map((course.value.chapters ?? []).map((chapter) => [chapter.id, chapter]));
    const groupsById = new Map((course.value.chapter_groups ?? []).map((group) => [group.id, group]));
    const structure = (course.value.chapter_structure?.length ?? 0) > 0
        ? course.value.chapter_structure
        : (course.value.chapters ?? []).map((chapter) => ({ type: 'chapter', chapter_id: chapter.id }));
    const requiresSequentialUnlock = course.value.type === 'class' && course.value.chapters_must_be_consumed_in_order;
    let previousChapterCanUnlockNext = true;

    return structure.map((item) => {
        if (item.type === 'group') {
            const group = groupsById.get(item.group_id);
            if (!group) {
                return null;
            }

            return {
                kind: 'group',
                id: group.id,
                title: group.title,
            };
        }

        const chapter = chaptersById.get(item.chapter_id);
        if (!chapter) {
            return null;
        }

        const available = requiresSequentialUnlock ? previousChapterCanUnlockNext : chapter.available;
        previousChapterCanUnlockNext = available && chapter.completed;

        return {
            kind: 'chapter',
            id: chapter.id,
            chapter,
            available,
        };
    }).filter((row) => row !== null);
});

const visibleRows = computed(() => {
    if (!shouldEnableCollapsibleGroups.value) {
        return displayRows.value;
    }

    return displayRows.value.filter((row) => {
        if (row.kind === 'group') {
            return true;
        }

        const chapterGroupId = row.chapter.course_chapter_group_id;
        if (!chapterGroupId) {
            return true;
        }

        return isGroupExpanded(chapterGroupId);
    });
});

const updateCompletion = async (chapter) => {
    const res = await axios.post(route('api.course.customerarea.chapter.toggle-completed', [props.courseId, chapter.id]))
    chapter.completed = res.data.completed
    emit('chapter-completion-update', chapter)
}

onMounted(() => {
    loadChapters()
})

watch([shouldEnableCollapsibleGroups, currentChapterGroupId], () => {
    ensureCurrentChapterGroupExpanded()
})

</script>
<template>
    <div
        class="lg:w-72 lg:shrink-0"
    >
        <span
            class="lg:hidden fixed bottom-4 right-4 z-modal-toggler"
        >
            <button-secondary
                @click="open = !open"
            >
                <span class="block relative">
                    <span :class="open ? 'opacity-0' : ''">{{ _mft('course:showChapters') }}</span>
                    <span
                        v-if="open"
                        class="block bg-white absolute inset-0 rounded-wl"
                    >
                        {{ _mft('shared:modal.close')}}
                    </span>
                </span>
            </button-secondary>
        </span>
        <div
            class="fixed top-16 bottom-0 z-modal-backdrop bg-black transition-opacity lg:hidden"
            :class="open ? 'left-0 w-screen opacity-10' : 'left-full opacity-0'"
            @click="open = false"
        >
        </div>
        <div
            class="w-4/5 fixed lg:sticky lg:w-72 top-4 bottom-0 transition-all z-modal lg:z-auto max-h-screen overflow-y-scroll pb-24"
            :class="open ? 'right-0' : '-right-full lg:left-0 lg:right-auto'"
        >
            <div
                v-if="course"
                class="bg-white py-4 rounded-wl shadow-xl mt-4 mx-1"
            >
                <div class="px-1 mb-4 text-center">
                    <h4 class="font-bold">
                        <a :href="route('course.customerarea.show', course.id)" class="text-inherit text-sm mf-link-immune">
                            <span class="text-md">{{ course.title }}</span>
                        </a>
                    </h4>
                </div>
                <ul class="divide-y">
                    <li
                        v-for="row in visibleRows"
                        :key="`${row.kind}-${row.id}`"
                        class="leading-5 overflow-hidden"
                    >
                        <div
                            v-if="row.kind === 'group'"
                            class="flex items-center justify-between text-xs tracking-wide font-semibold px-2 py-2 transition-colors"
                            :class="[isGroupExpanded(row.id) ? 'bg-wlPrimary text-wlPrimaryContrast' : 'text-gray-600', shouldEnableCollapsibleGroups ? 'cursor-pointer' : '']"
                            @click="toggleGroupExpansion(row.id)"
                        >
                            <span class="pr-2">{{ row.title }}</span>
                            <span
                                v-if="shouldEnableCollapsibleGroups"
                                class="ml-auto pl-2"
                            >
                                <i :class="isGroupExpanded(row.id) ? 'fa-solid fa-minus' : 'fa-solid fa-chevron-down'"></i>
                            </span>
                        </div>

                        <div
                            v-else
                            class="flex gap-1 w-full px-2 py-2"
                        >
                            <div
                                v-if="row.chapter.thumbnail_url"
                                class="w-12 h-12 flex-shrink-0 pr-1"
                            >
                                <img
                                    :src="row.chapter.thumbnail_url"
                                    :class="row.chapter.published && row.available ? '' : 'opacity-50'"
                                    alt=""
                                    class="w-12 h-12 object-cover rounded"
                                />
                            </div>

                            <div class="flex-1 min-w-0">
                                <div>
                                    <component
                                        :is="row.chapter.published && row.available ? 'a' : 'span'"
                                        class="text-xs mf-link-immune leading-4 block"
                                        :href="route('course.customerarea.chapter.show', [course.id, row.chapter.id])"
                                        :class="[row.chapter.published && row.available ? '' : 'cursor-not-allowed opacity-70', row.chapter.id === currentChapterId ? 'font-semibold text-black' : 'font-medium']"
                                    >
                                        {{ row.chapter.title }}
                                    </component>
                                    <div
                                        v-if="row.chapter.description"
                                        class="text-gray-500 leading-4 mt-1"
                                    >
                                        <div class="block text-xxs">
                                            {{ row.chapter.description }}
                                        </div>
                                        <div
                                            v-if="course.has_multiple_providers && row.chapter.provider"
                                            class="mt-1 text-gray-400 text-xxs block"
                                        >{{ _mft('shared:by') }} {{ row.chapter.provider.name }}</div>
                                    </div>
                                </div>
                            </div>

                            <div
                                v-if="row.available"
                                class="w-6 flex-shrink-0"
                            >
                                <toggle-done
                                    v-model:content="row.chapter.completed"
                                    @update:content="updateCompletion(row.chapter)"
                                    :disabled="! row.chapter.published"
                                    class="w-4 h-4 rounded-sm ml-1 shrink-0 mt-1"
                                >Toggle</toggle-done>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>
